import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './AllExperiencesPopup.css';

const AllExperiencesPopup = ({ isOpen, onClose, userExperiences, onEditExperience, onDeleteExperience }) => {
  const [allExperiences, setAllExperiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Format date range for better readability
  const formatDateRange = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    const end = isCurrent ? 'Present' : formatDate(endDate);
    
    return `${start} - ${end}`;
  };

  // Format date range with styled Present badge
  const formatDateRangeWithBadge = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    
    if (isCurrent) {
      return (
        <>
          {start} - <span className="present-badge">Present</span>
        </>
      );
    } else {
      const end = formatDate(endDate);
      return `${start} - ${end}`;
    }
  };

  // Fetch all experiences when popup opens
  useEffect(() => {
    if (isOpen) {
      fetchAllExperiences();
    }
  }, [isOpen]);

  const fetchAllExperiences = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.get('/experiences');
      if (response.success) {
        setAllExperiences(response.data || []);
      } else {
        setError('Failed to load experiences');
      }
    } catch (err) {
      setError('Error loading experiences: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Use experiences as they come from API (already sorted by backend)
  const sortedExperiences = allExperiences;

  if (!isOpen) return null;

  return (
    <div className="all-experiences-popup-overlay">
      <div className="all-experiences-popup">
        <div className="all-experiences-popup-header">
          <div className="all-experiences-popup-title">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                <path d="M6 21v-2a4 4 0 0 1 4-4h.5"></path>
              </svg>
            </div>
            <div className="header-content">
              <h3>All Professional Experiences</h3>
              <p>Complete career history ({sortedExperiences.length} experiences)</p>
            </div>
          </div>
          <button className="all-experiences-popup-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="all-experiences-popup-content">
          {loading ? (
            <div className="all-experiences-loading">
              <div className="spinner-large"></div>
              <span>Loading all experiences...</span>
            </div>
          ) : error ? (
            <div className="all-experiences-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <h4>Error Loading Experiences</h4>
              <p>{error}</p>
              <button className="retry-btn" onClick={fetchAllExperiences}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23,4 23,10 17,10"></polyline>
                  <polyline points="1,20 1,14 7,14"></polyline>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                </svg>
                Try Again
              </button>
            </div>
          ) : sortedExperiences.length === 0 ? (
            <div className="all-experiences-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                <path d="M6 21v-2a4 4 0 0 1 4-4h.5"></path>
              </svg>
              <h4>No Experiences Found</h4>
              <p>Start building your professional profile by adding your first experience.</p>
            </div>
          ) : (
            <div className="all-experiences-list">
              {sortedExperiences.map((experience, index) => (
                <div key={experience.id} className={`all-experience-card ${experience.is_current ? 'current-job' : ''}`} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="all-experience-header">
                    <div className="all-experience-title-section">
                      {experience.company_logo && (
                        <img 
                          src={experience.company_logo} 
                          alt={`${experience.company} logo`}
                          className="all-experience-company-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="all-experience-title-content">
                        <h4 className="all-experience-title">
                          {experience.title}
                        </h4>
                        <span className="all-experience-company">{experience.company}</span>
                      </div>
                    </div>
                    <div className="all-experience-actions">
                      <button 
                        className="all-edit-experience-btn"
                        onClick={() => {
                          onEditExperience(experience);
                          onClose();
                        }}
                        title="Edit experience"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                        </svg>
                      </button>
                      <button 
                        className="all-remove-experience-btn"
                        onClick={() => onDeleteExperience(experience.id)}
                        title="Remove experience"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="all-experience-details">
                    <div className="all-experience-meta">
                      <div className="all-experience-duration">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12,6 12,12 16,14"></polyline>
                        </svg>
                        <span>
                          {experience.date_range || formatDateRangeWithBadge(experience.start_date, experience.end_date, experience.is_current)}
                        </span>
                      </div>
                      {experience.location && (
                        <div className="all-experience-location">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                          <span>{experience.location}</span>
                        </div>
                      )}
                      <div className="all-experience-type">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                          <line x1="8" y1="21" x2="16" y2="21"></line>
                          <line x1="12" y1="17" x2="12" y2="21"></line>
                        </svg>
                        <span>{experience.formatted_employment_type || experience.employment_type}</span>
                      </div>
                    </div>

                    {experience.description && (
                      <div className="all-experience-description">
                        <p>{experience.description}</p>
                      </div>
                    )}

                    {experience.skills_used && experience.skills_used.length > 0 && (
                      <div className="all-experience-skills">
                        <h5>Skills Used:</h5>
                        <div className="all-skills-tags">
                          {experience.skills_used.map((skill, skillIndex) => (
                            <span key={skillIndex} className="all-skill-tag">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="all-experiences-popup-footer">
          <button className="all-experiences-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllExperiencesPopup;
